import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { CustomerDataModel, CustomerModel, DataViewEnum, ColumnModel, ClaimModel, CustomerPolicyModel, IncidentModel } from '../models';
import { setFormattedData, addOrUpdateData } from '../helpers/helper';

export interface CustomersState {
  dataView: DataViewEnum;
  data: CustomerDataModel;
}

const initialState: CustomersState = {
  dataView: DataViewEnum.List,

  data: {
    recordCount: 0,
    records: [],
    rawData: [],
    columns: []
  },
};

const uniqueDataKey = "customerId";

const columnConfig: Record<string, Partial<ColumnModel>> = {
  primaryKeyId: {
      width: 60,
      formatter: (record: CustomerModel) => record.customerId
  },
  name: {
    width: 160,
    formatter: (record: CustomerModel) => `${record.firstName ?? ""} ${record.middleName ?? ""} ${record.lastName ?? ""}`
  },
  email: {
    width: 200
  },
  address: {
    width: 300,
    hasTooltip: true,
    formatter: (record: CustomerModel) => `${record.address1 ?? ""} ${record.address2 ?? ""} ${record.city ?? ""} ${record.county ?? ""} ${record.country ?? ""} ${record.postCode ?? ""}`
  },
  phoneNumber: {
    width: 200
  },
  lastContactedOn: {
    width: 180,
    formatter: (record: CustomerModel) => record.lastContactedOn ? new Date(record.lastContactedOn).toLocaleString() : ''
  },
};

export const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    setDataView: (state, action: PayloadAction<DataViewEnum>) => {
      state.dataView = action.payload;
      return state;
    },
    setCustomersData: (state, action: PayloadAction<CustomerModel[]>) => {
      state.data = setFormattedData({ rawData: action.payload }, columnConfig, uniqueDataKey);
    },
    setCustomerClaimsPolicies: (state, action: PayloadAction<{ customerId: number, claims: ClaimModel[], policies: CustomerPolicyModel[], incidents: IncidentModel[]}>) => {
      // update the state.data.records row that has matching customerId with a children property that contains claims object and policies object
      const index = state.data.records.findIndex(record => record[uniqueDataKey] === action.payload.customerId);
      const policy = action.payload.policies[0].policy;

      if (index !== -1) {

        state.data.records[index].children = [
          {
            title: "Policies",
            records: [policy],
            columns: [
              { key: "policyId", title: "ID", width: 60 },
              { key: "policyNumber", title: "Policy Number", width: 130 },
              { key: "coverageDetails", title: "Coverage Details", width: 300, hasTooltip: true, },
              { key: "policyLimits", title: "Policy Limits", width: 300, hasTooltip: true, },
              { key: "premiumInformation", title: "Premium Information", width: 200, hasTooltip: true },
              { key: "specialConditions", title: "Special Conditions", width: 400, hasTooltip: true, flexGrow: 1 },
            ]
          },
          {
            title: "Incidents",
            records: action.payload.incidents,
            columns: [
              { key: "incidentId", title: "ID", width: 60 },
              { key: "incidentDateTime", title: "Date/Time", width: 200 },
              { key: "location", title: "Location", width: 260, hasTooltip: true },
              { key: "description", title: "Description", width: 300, hasTooltip: true },
              { key: "otherVehiclesPartiesInvolved", title: "Involved", width: 200, hasTooltip: true },
              { key: "witnessInformation", title: "Witness Information", width: 200, hasTooltip: true },
              { key: "policeReport", title: "Police Report", width: 200, hasTooltip: true }
            ]
          },
          {
            title: "Claims",
            records: action.payload.claims,
            columns: [
              { key: "claimId", title: "Claim ID", width: 100 },
              { key: "claimType", title: "Claim Type", width: 200 },
              { key: "claimStatus", title: "Claim Status", width: 200 },
              { key: "claimDate", title: "Claim Date", width: 200 },
              { key: "claimAmount", title: "Claim Amount", width: 200 },
            ]
          }
        ];
      }
    },
    setCustomer: (state, action: PayloadAction<CustomerModel>) => {
      if (state.data.columns.length === 0) {
        state.data = setFormattedData({ rawData: [action.payload] }, columnConfig, uniqueDataKey);
      } else {
        addOrUpdateData(state.data, action.payload, columnConfig, uniqueDataKey);
      }
      return state;
    },
    removeCustomer: (state, action: PayloadAction<number>) => {
      state.data.records = state.data.records.filter(record => record[uniqueDataKey] !== action.payload);
      state.data.rawData = state.data.rawData.filter(raw => raw[uniqueDataKey] !== action.payload);
      return state
    },
    setLogoutCustomers: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const { setDataView, setCustomersData, setCustomerClaimsPolicies, setCustomer, removeCustomer, setLogoutCustomers } = customersSlice.actions;

export default customersSlice.reducer;